import useDepartureWorkspaceMutations from 'api/operation/departure/useDepartureWorkspaceMutations';
import useGetDepartureWorkspace from 'api/operation/departure/useGetDepartureWorkspace';
import { useCallback, useEffect, useMemo, useState } from 'react';

const useDepartureWorkspace = guid => {
  const { getDepartureWorkspace, loading, workspace } =
    useGetDepartureWorkspace();
  const mutations = useDepartureWorkspaceMutations();
  const [actionLoading, setActionLoading] = useState(false);

  const canRefetch = useMemo(
    () => guid && location.pathname.startsWith('/operation/departure'),
    [guid],
  );

  useEffect(() => {
    if (!canRefetch) return;
    getDepartureWorkspace(guid);
  }, [canRefetch, guid]);

  const refreshWorkspace = useCallback(() => {
    if (!guid) return Promise.resolve(null);
    return getDepartureWorkspace(guid);
  }, [getDepartureWorkspace, guid]);

  useEffect(() => {
    if (!canRefetch) return;

    window.addEventListener('refreshOperationList', refreshWorkspace);
    return () => {
      window.removeEventListener('refreshOperationList', refreshWorkspace);
    };
  }, [refreshWorkspace, canRefetch]);

  const withAction = useCallback(
    async action => {
      setActionLoading(true);
      try {
        const result = await action();
        if (result) await refreshWorkspace();
        return result;
      } finally {
        setActionLoading(false);
      }
    },
    [refreshWorkspace],
  );

  // agentList, exitList, docker, title, state, hasVoucher, voucherType
  const agents = useMemo(
    () =>
      (workspace?.agentList ?? []).map(agent => ({
        ...agent,
        id: agent.operationAgentGuid || agent.guid,
      })),
    [workspace?.agentList],
  );

  const exitProducts = useMemo(
    () =>
      (workspace?.exitList ?? []).map(item => ({
        ...item,
        id: item.guid || item.operationProductGuid,
      })),
    [workspace?.exitList],
  );

  const dockerBaskets = useMemo(
    () =>
      (workspace?.docker ?? []).map(basket => ({
        ...basket,
        id: basket.guid,
      })),
    [workspace?.docker],
  );

  const state = workspace?.state;
  const hasVoucher = Boolean(workspace?.hasVoucher);
  const voucherType = workspace?.voucherType ?? 0;
  const title = workspace?.title ?? '';

  const isLocked = state === 2 || state === 3;
  const canSubmitOperation = !isLocked;
  const canSubmitVoucher = state === 3 && !hasVoucher && voucherType !== 0;
  const canMutate = !isLocked;

  const autoInitAll = useCallback(
    () => withAction(() => mutations.autoInitAll(guid)),
    [guid, mutations, withAction],
  );

  const autoInit = useCallback(
    payload =>
      withAction(() =>
        mutations.autoInit({
          ...payload,
          operationGuid: guid,
        }),
      ),
    [guid, mutations, withAction],
  );

  const initTask = useCallback(
    payload =>
      withAction(() =>
        mutations.initTask({
          ...payload,
          operationGuid: guid,
        }),
      ),
    [guid, mutations, withAction],
  );

  const getAllLocality = useCallback(
    (zoneGuid, productPackGuid) =>
      mutations.getAllLocality(zoneGuid, productPackGuid),
    [mutations],
  );

  const insertAgent = useCallback(
    agentGuid =>
      withAction(() =>
        mutations.insertAgent({ operationGuid: guid, agentGuid }),
      ),
    [guid, mutations, withAction],
  );

  const deleteAgent = useCallback(
    operationAgentGuid =>
      withAction(() => mutations.deleteAgent(operationAgentGuid)),
    [mutations, withAction],
  );

  const removeAgentTask = useCallback(
    agentTaskGuid => withAction(() => mutations.removeAgentTask(agentTaskGuid)),
    [mutations, withAction],
  );

  const getAgentDetail = useCallback(
    operationAgentGuid => mutations.getAgentDetail(operationAgentGuid),
    [mutations],
  );

  const runTask = useCallback(
    taskGuid => withAction(() => mutations.runTask(taskGuid)),
    [mutations, withAction],
  );

  const runAllTasks = useCallback(
    agentGuid => withAction(() => mutations.runAllTasks(agentGuid, guid)),
    [guid, mutations, withAction],
  );

  const getShoppingCartDetail = useCallback(
    shoppingCartGuid => mutations.getShoppingCartDetail(shoppingCartGuid),
    [mutations],
  );

  const confirmMission = useCallback(
    taskGuid => withAction(() => mutations.confirmMission(taskGuid)),
    [mutations, withAction],
  );

  const confirmAllMissions = useCallback(
    operationShoppingCartGuid =>
      withAction(() => mutations.confirmAllMissions(operationShoppingCartGuid)),
    [mutations, withAction],
  );

  const submitOperation = useCallback(
    () => withAction(() => mutations.submitOperation(guid)),
    [guid, mutations, withAction],
  );

  const submitVoucher = useCallback(
    () => withAction(() => mutations.submitVoucher(guid)),
    [guid, mutations, withAction],
  );

  const getAvailableAgents = useCallback(
    zoneGuid => mutations.getAvailableAgents(zoneGuid),
    [],
  );

  return {
    loading,
    actionLoading,
    workspace,
    title,
    state,
    hasVoucher,
    voucherType,
    agents,
    exitProducts,
    dockerBaskets,
    refreshWorkspace,
    isLocked,
    canSubmitOperation,
    canSubmitVoucher,
    canMutate,
    autoInitAll,
    autoInit,
    initTask,
    getAllLocality,
    insertAgent,
    deleteAgent,
    removeAgentTask,
    getAgentDetail,
    runTask,
    runAllTasks,
    getShoppingCartDetail,
    confirmMission,
    confirmAllMissions,
    submitOperation,
    submitVoucher,
    getAvailableAgents,
  };
};

export default useDepartureWorkspace;
